import { useMemo } from 'react';

import { ConsoleAPIGraph } from '@adflow/graphqlgen';
import {
  AdElement,
  AdElementDataSource,
  AdElementImage,
  AdElementVector,
  AdElementVectorColor,
  AdflowAPI,
  Console,
  DateTimeFormat,
  isDisplayOptionsOddsType,
  isImageElement,
  isTeamAssetElement,
  isCasinoAssetElement,
  isTextElement,
  isVectorElement,
} from '@adflow/types';
import { fmtReference, formatOdds } from '@adflow/utils';
import {
  MockPreviewValues,
  MockTeamAssets,
  MockVectorPalettes,
  getPreviewDate,
  getMockScoreValue,
  isScoreKey,
} from './previewConstants';

export function useMappedElements(
  elements: AdElement[],
  displayOptions?: AdflowAPI.AdTemplateDisplayOptions | null,
  brand?: ConsoleAPIGraph.Brand | null,
  dataSources?: Array<{ id: string; type: string }>,
) {
  const sourceTypeBySourceId = useMemo(() => {
    if (!dataSources || dataSources.length === 0) return undefined;
    const map = new Map<string, string>();
    for (const ds of dataSources) {
      map.set(ds.id, ds.type);
    }
    return map;
  }, [dataSources]);
  const templateDateTimeFormat = useMemo(
    () =>
      displayOptions?.dateTimeFormat ||
      ({
        timeFormat: 'hour24',
        dayFormat: 'long',
        monthFormat: 'long',
        showTimezone: false,
        countdownFormatStyle: 'digital',
        countdownTimeGranularity: 'H_M_S',
      } as DateTimeFormat),
    [displayOptions?.dateTimeFormat],
  );

  // new oddsDisplay format is set in displayOptions and is decimalOdds by defaults
  const oddsDisplayFormat = useMemo(
    () =>
      displayOptions?.odds?.type && isDisplayOptionsOddsType(displayOptions.odds.type)
        ? displayOptions.odds.type
        : 'decimalOdds',
    [displayOptions?.odds?.type],
  );

  const potentialWinnings = useMemo(() => {
    const oddsDisplayOptions =
      displayOptions?.odds?.stake && displayOptions?.odds?.currency ? displayOptions.odds : null;
    return (
      oddsDisplayOptions || brand?.adTemplate?.potentialWinnings || { stake: 10, currency: 'GBP' }
    );
  }, [brand?.adTemplate?.potentialWinnings, displayOptions?.odds]);

  const mappedElements = useMemo(
    () =>
      elements.map((el) => {
        const cloned = structuredClone(el) as AdElement;

        if (isTextElement(cloned)) {
          const dateTimeFormat: DateTimeFormat =
            cloned.data.dateDisplayOptions || templateDateTimeFormat;

          cloned.data.content = templateString(
            cloned.data.content,
            cloned.sources,
            cloned.data.odds?.type || oddsDisplayFormat,
            cloned.data.odds || potentialWinnings,
            dateTimeFormat,
            sourceTypeBySourceId,
          );
        }

        if (isTeamAssetElement(cloned)) {
          const assetType = displayOptions?.teamAssets?.type ?? 'JERSEY';

          return {
            ...cloned,
            data: {
              content: cloned.data.content,
              source: cloned.data.source,
              display:
                cloned.data.source === 'HOME_ASSET' || cloned.data.source === 'COMPETITOR_ASSET'
                  ? MockEventDataSource.HOME_ASSET[assetType]
                  : MockEventDataSource.AWAY_ASSET[assetType],
            },
          };
        }

        if (isCasinoAssetElement(cloned)) {
          return {
            ...cloned,
          };
        }

        if (isVectorElement(cloned)) {
          const fill = cloned.data.color.fill;
          const stroke = cloned.data.color.stroke;
          fill && updateVectorColor(fill, 0);
          stroke && updateVectorColor(stroke, 1);

          return {
            ...cloned,
            data: {
              ...cloned.data,
              color: {
                fill,
                stroke,
                strokeWeight: cloned.data.color.strokeWeight,
              },
            },
          } satisfies AdElementVector;
        }

        if (isImageElement(cloned)) {
          return {
            ...cloned,
            data: {
              ...cloned.data,
              content: cloned.data.initialContent ?? cloned.data.content,
            },
          } satisfies AdElementImage;
        }

        return cloned;
      }),
    [
      elements,
      oddsDisplayFormat,
      potentialWinnings,
      displayOptions,
      templateDateTimeFormat,
      sourceTypeBySourceId,
    ],
  );
  return mappedElements;
}

function templateString(
  templatedStr: string,
  sources: Array<AdElementDataSource>,
  oddsDisplayFormat: Console.OddsType = 'decimalOdds',
  potentialWinnings: ConsoleAPIGraph.PotentialWinnings | AdflowAPI.AdTemplateDisplayOptions['odds'],
  dateTimeFormat: DateTimeFormat,
  sourceTypeBySourceId?: Map<string, string>,
) {
  let replacedStr = templatedStr;

  sources.forEach((dSource) => {
    const isTextDate = dSource.name === 'DATE' && dateTimeFormat?.isTextFormat;
    const is12HourDate = dateTimeFormat.timeFormat === 'hour12';

    const name = isTextDate ? (is12HourDate ? 'TEXT_DATE_12' : 'TEXT_DATE') : dSource.name;

    if (isScoreKey(name)) {
      const sourceType = sourceTypeBySourceId?.get(dSource.sourceId);
      const scoreValue = getMockScoreValue(name, sourceType);
      if (scoreValue !== undefined) {
        replacedStr = replacedStr.replace(`{${dSource.sourceId}:${dSource.name}}`, scoreValue);
        return;
      }
    }

    const data = MockEventDataSource[name];
    let dataString = '';

    switch (typeof data) {
      case 'string':
        dataString = data;
        break;
      case 'object':
        if (data instanceof Date) {
          dataString = fmtReference(data, locale, 'Etc/UTC', dSource.name, dateTimeFormat);
        } else {
          const calculateDecimalPlaces =
            potentialWinnings && 'calculateDecimalPlaces' in potentialWinnings
              ? potentialWinnings.calculateDecimalPlaces
              : false;
          dataString = formatOdds(
            [...data],
            oddsDisplayFormat,
            potentialWinnings?.stake,
            potentialWinnings?.currency,
            locale,
            undefined,
            calculateDecimalPlaces,
          );
        }
        break;
    }

    replacedStr = replacedStr.replace(`{${dSource.sourceId}:${dSource.name}}`, dataString);
  });

  return replacedStr;
}

/**
 *
 * @param color
 * @param palette which colors do you desire?
 */

function updateVectorColor(color: AdElementVectorColor, palette: 0 | 1) {
  if (color.source && color.type) {
    color.value = MockVectorPalettes[palette][color.source]?.[color.type] ?? '';
  }
}

// Client-side specific: use browser's locale
const locale = navigator.language || 'en-GB';

// Build MockEventDataSource by combining shared constants with client-specific date values
const previewDate = getPreviewDate();

// in the ad server, each of these keys would be a method for retrieving the
// related data from their source e.g event payload.
const MockEventDataSource = {
  // Spread shared constants (strings, numbers, arrays)
  ...MockPreviewValues,
  // Team assets from shared constants
  HOME_ASSET: MockTeamAssets.HOME_ASSET,
  AWAY_ASSET: MockTeamAssets.AWAY_ASSET,
  // Client-side specific: Date objects for formatting with fmtReference
  DAY: previewDate,
  DATE: previewDate,
  MONTH: previewDate,
  TIME: previewDate,
  COUNTDOWN: previewDate,
  PRE_COUNTDOWN: previewDate,
} as const;
